import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdAnalytics from './AdAnalytics';
import TabPanel from '../common/TabPanel';

// Mock data for SDK configurations
const SDK_CONFIGS = [
  { value: 'react', label: 'React' },
  { value: 'vue', label: 'Vue.js' },
  { value: 'angular', label: 'Angular' },
  { value: 'next', label: 'Next.js' }
];

// Mock data for ad placements
const AD_PLACEMENTS = [
  { value: 'header', label: 'Header' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'content', label: 'Content' },
  { value: 'footer', label: 'Footer' }
];

interface PublisherPlaygroundProps {
  onRoleSwitch: () => void;
  onReset: () => void;
}

const PublisherPlayground: React.FC<PublisherPlaygroundProps> = ({ onRoleSwitch, onReset }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedSDK, setSelectedSDK] = useState<string>('');
  const [selectedPlacement, setSelectedPlacement] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sdkCode, setSdkCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSDKChange = (event: SelectChangeEvent) => {
    setSelectedSDK(event.target.value);
  };

  const handlePlacementChange = (event: SelectChangeEvent) => {
    setSelectedPlacement(event.target.value);
  };

  const handleGenerateCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock SDK code based on selection
      const code = `// ${selectedSDK.toUpperCase()} SDK Integration
import { ReasonicSDK } from '@reasonic/sdk';

const sdk = new ReasonicSDK({
  apiKey: 'YOUR_API_KEY',
  placement: '${selectedPlacement}',
  environment: 'production'
});

// Initialize the SDK
sdk.init();

// Handle ad events
sdk.on('adLoaded', (ad) => {
  console.log('Ad loaded:', ad);
});

sdk.on('adError', (error) => {
  console.error('Ad error:', error);
});`;
      
      setSdkCode(code);
    } catch (err) {
      setError('Failed to generate SDK code. Please try again.');
      console.error('Error generating SDK code:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (sdkCode) {
      navigator.clipboard.writeText(sdkCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
          Publisher Playground
        </Typography>
        <Box>
          <Tooltip title="Switch to Advertiser view">
            <Button 
              onClick={onRoleSwitch}
              startIcon={<SwapHorizIcon />}
              sx={{ mr: 2, color: '#10B981' }}
            >
              Switch to Advertiser
            </Button>
          </Tooltip>
          <Tooltip title="Reset playground">
            <Button 
              onClick={onReset}
              startIcon={<RefreshIcon />}
              sx={{ color: '#94A3B8' }}
            >
              Reset
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ mt: 4, mb: 4 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="publisher playground tabs"
          centered
        >
          <Tab label="Preview" />
          <Tab label="Analytics" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>SDK Configuration</Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}>
                    {error}
                  </Alert>
                )}
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="sdk-select-label">Select SDK</InputLabel>
                  <Select
                    labelId="sdk-select-label"
                    id="sdk-select"
                    value={selectedSDK}
                    label="Select SDK"
                    onChange={handleSDKChange}
                    sx={{
                      backgroundColor: '#1E293B',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148, 163, 184, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                    }}
                  >
                    {SDK_CONFIGS.map((sdk) => (
                      <MenuItem key={sdk.value} value={sdk.value}>
                        {sdk.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="placement-select-label">Select Placement</InputLabel>
                  <Select
                    labelId="placement-select-label"
                    id="placement-select"
                    value={selectedPlacement}
                    label="Select Placement"
                    onChange={handlePlacementChange}
                    sx={{
                      backgroundColor: '#1E293B',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148, 163, 184, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                    }}
                  >
                    {AD_PLACEMENTS.map((placement) => (
                      <MenuItem key={placement.value} value={placement.value}>
                        {placement.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button 
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGenerateCode}
                  disabled={!selectedSDK || !selectedPlacement || isLoading}
                  sx={{ 
                    backgroundColor: '#3B82F6',
                    '&:hover': { backgroundColor: '#2563EB' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(59, 130, 246, 0.3)' }
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Generate SDK Code'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper 
                elevation={3} 
                sx={{ 
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  p: 3,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    Generated Code
                  </Typography>
                  {sdkCode && (
                    <Tooltip title={copied ? "Copied!" : "Copy code"}>
                      <IconButton 
                        onClick={handleCopyCode}
                        sx={{ color: copied ? '#10B981' : '#94A3B8' }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                {!sdkCode ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    py: 5
                  }}>
                    <Typography sx={{ color: '#94A3B8', mb: 2, textAlign: 'center' }}>
                      No code generated yet.
                    </Typography>
                    <Typography sx={{ color: '#64748B', textAlign: 'center' }}>
                      Select an SDK and placement, then click "Generate SDK Code" to see the integration code.
                    </Typography>
                  </Box>
                ) : (
                  <Card sx={{ backgroundColor: '#0F172A', borderRadius: 2 }}>
                    <CardContent>
                      <pre style={{ 
                        color: '#E2E8F0', 
                        margin: 0, 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}>
                        {sdkCode}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <AdAnalytics role="publisher" />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default PublisherPlayground; 